import SectionPanel from '@/components/SectionPanel';
import WaitingSpinner from "@/components/WaitingSpinner";
import { Flex, Stack, Skeleton, VStack, HStack, Text } from "@chakra-ui/react";
import type { DataTableCellContext as CellContext, DataTableColumnDef as ColumnDef, DataTableRow as Row } from "src/components/DataTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Money } from "src/api/Money";
import { ProjectBalance } from "src/api/ProjectBalance";
import { ProjectTotal } from "src/api/ProjectTotal";
import Content from "src/components/Content"
import DataTable, { DataTableToolbar, amountHeader, moneyCell, moneyListCell, moneySort, moneyText } from "src/components/DataTable";
import { ProgressBarReact } from "src/components/ProgressBar";
import ReachLink from "src/components/ReachLink";
import { reportService } from "src/service/ReportService";
import { Switch } from "@/components/ui/switch"
import CountryFlag from "@/components/CountryFlag";
import { countries } from 'src/data/Countries';
import { useI18n } from '@/i18n/I18nContext';

const DashboardProjectsAll = () => {
    const { t } = useI18n();
    const [data, setData] = useState<ProjectTotal[]>([]);
    const [active, setActive] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    const loadAllData = (): Promise<void> => {
        setIsLoading(true);
        return reportService.projectAll()
            .then(r => setData(r.overall.concat()))
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        loadAllData();
    }, []);

    const onView = useCallback((id: number): void => {
        console.info(`viewing ${id}`);
        navigate(`/projects/overview/projects/${id}`);
    }, [navigate]);

    const onFilter = (row: ProjectTotal, value: string): boolean => {
        //console.log(`row=${row.project.name} value=${value}`);
        if (value === 'active') {
            return row.project.active;
        }
        return true;
    }

    const columns: ColumnDef<ProjectTotal>[] = useMemo(
        () => [
            {
                id: 'country',
                header: 'Land',
                accessorKey: 'project.country',
                cell: (info: CellContext<ProjectTotal, string>) => (
                    <HStack>
                        <CountryFlag code={info.getValue()} />
                        <Text>{countries[info.getValue()]}</Text>
                    </HStack>
                ),
                sortFn: (rowA: Row<ProjectTotal>, rowB: Row<ProjectTotal>, _: any): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = countries[a.project.country] ?? '';
                    let bp = countries[b.project.country] ?? '';
                    return ap.trim().localeCompare(bp.trim(), undefined, { sensitivity: 'base' });
                },
            },
            {
                id: 'project',
                header: 'Projekt',
                accessorKey: 'project.name',
                cell: (info) => {
                    let row = info.row.original;
                    return (<ReachLink to={`/projects/view/${row.project.id}`} name={row.project.name} />);
                },
            },
            {
                id: 'budget',
                header: amountHeader({ name: ['Projekt Budget', '(BUD WK)'] }),
                accessorKey: 'balance.budget',
                cell: moneyCell(),
                sortFn: moneySort(),
            },
            {
                id: 'transfer',
                header: amountHeader({ name: ['Transfer', '(Aktueller WK)'], explanation: 'Transfers an Partner und Konsulenten (in grün)' }),
                accessorFn: (row: ProjectTotal) => row.balance,
                cell: (info: CellContext<ProjectTotal, ProjectBalance>) => {
                    let balance = info.getValue();
                    return <VStack>
                        {moneyText(balance.transfer, '10rem', 'normal')}
                        {balance.transferConsultant.value !== 0 && <Flex color='green' fontSize='small'>{moneyText(balance.transferConsultant, '10rem', 'normal')}</Flex>}
                    </VStack>
                }
            },
            {
                id: 'localBudget',
                header: amountHeader({ name: 'Lokal Budget' }),
                accessorKey: 'balance.localBudget',
                cell: moneyCell(),
                sortFn: moneySort(),
            },
            {
                id: 'localTransfers',
                header: amountHeader({ name: 'Lokal Transfer', explanation: 'Transfers an Partner und Konsulenten (in grün) in Fremdwährungen' }),
                accessorFn: (row: ProjectTotal) => row.balance,
                cell: (info: CellContext<ProjectTotal, ProjectBalance>) => {
                    let balance = info.getValue();
                    return <VStack>
                        {moneyListCell(balance.localTransfers, info.row.original.project.currency)}
                        {balance.localTransfersConsultant.length > 0 && <Flex color='green' fontSize='small'>{moneyListCell(balance.localTransfersConsultant, info.row.original.project.currency)}</Flex>}
                    </VStack>
                }
            },
            {
                id: 'transferCoverage',
                header: amountHeader({ name: 'Lokal Deckung %', explanation: 'Total Lokal Transfers / Total Lokal Budget' }),
                accessorKey: 'coverage.localTransferCoverage',
                cell: (info: CellContext<ProjectTotal, number>) => ProgressBarReact(info.getValue())
            },
            {
                id: 'localExpenses',
                header: amountHeader({ name: 'Lokal Ausgaben' }),
                accessorKey: 'balance.localExpenses',
                cell: (info: CellContext<ProjectTotal, Money[]>) => moneyListCell(info.getValue(), info.row.original.project.currency),
            },
            {
                id: 'expenseCoverage',
                header: amountHeader({ name: 'Ausgaben %', explanation: 'Total Lokal Ausgaben / Total Lokal Budget' }),
                accessorKey: 'coverage.expenseCoverage',
                cell: (info: CellContext<ProjectTotal, number>) => ProgressBarReact(info.getValue())
            },
        ], []
    );

    return (
        <Content title={t('reports.projects.title')} description={t('reports.projects.description')}>
            <Stack gap='6' minW='0'>
                <DataTableToolbar resultCount={data.filter(row => !active || row.project.active).length}>
                    <Switch id='activeOnly' colorPalette='blue' checked={active} onCheckedChange={event => setActive(event.checked)}>{t('projects.overview.activeOnly')}</Switch>
                </DataTableToolbar>
                <SectionPanel title={t('projects.report.budgetAndTransfers')}>
                    {isLoading ? (
                        <Stack py={5}>
                            <WaitingSpinner size='xl' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                        </Stack>
                    ) : (
                        <DataTable columns={columns} data={data} initialSortByColumn='country' initialSortByDesc={false}
                            hideColumnsOnXs={['country', 'transfer', 'localBudget', 'localTransfers', 'localExpenses']}
                            onFilter={onFilter} filter={active ? 'active' : ''}
                            onRowClick={row => onView(row.project.id)}
                        />
                    )}
                </SectionPanel>
            </Stack>
        </Content>
    )
}

export default DashboardProjectsAll
